/**
 * StatusBar.tsx
 * Barre de statut en bas de l'application (style Photoshop/Figma)
 * UX: Informations contextuelles (zoom, dimensions, sélection, etc.)
 */
import React, { useMemo, memo } from 'react';
import { useAppStore } from '../../../services/StateService';

/**
 * StatusBar - Barre de statut en bas de l'application
 * Phase 3.1 : Optimisé avec React.memo
 */
const StatusBarComponent: React.FC = () => {
  const zoomLevel = useAppStore(state => state.canvas.zoomLevel || 1);
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  // P0.2 FIX: Utiliser selection.primaryId au lieu de canvas.selectedNodeId
  const selectedNodeId = useAppStore(state => state.selection.primaryId);
  const shapes = useAppStore(state => state.shapes);
  const saveStatus = useAppStore(state => state.save.status);
  const isExporting = useAppStore(state => state.canvas.isExporting);

  const selectedShape = useMemo(
    () => (selectedNodeId ? shapes.find(s => s.id === selectedNodeId) : null),
    [selectedNodeId, shapes]
  );

  const selectedCount = useMemo(() => {
    return selectedNodeId ? 1 : 0;
  }, [selectedNodeId]);

  const formatZoom = (zoom: number) => {
    return `${Math.round(zoom * 100)}%`;
  };

  return (
    <div className="status-bar">
      <div className="status-bar-left">
        {/* Sélection */}
        {selectedCount > 0 && (
          <div className="status-item">
            <span className="status-label">
              {selectedCount} {selectedCount === 1 ? 'élément' : 'éléments'} sélectionné
              {selectedCount > 1 ? 's' : ''}
            </span>
            {selectedShape && (
              <span className="status-value">
                {selectedShape.type} • {selectedShape.name || 'Sans nom'}
              </span>
            )}
          </div>
        )}

        {/* Dimensions du template */}
        <div className="status-item">
          <span className="status-label">Dimensions:</span>
          <span className="status-value">
            {templateWidth} × {templateHeight} px
          </span>
        </div>
      </div>

      <div className="status-bar-right">
        {/* Zoom */}
        <div className="status-item">
          <span className="status-label">Zoom:</span>
          <span className="status-value">{formatZoom(zoomLevel)}</span>
        </div>

        {/* Statut de sauvegarde */}
        <div className={`status-item status-save ${saveStatus === 'saved' ? 'saved' : 'unsaved'}`}>
          {saveStatus === 'saved' ? (
            <>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>Sauvegardé</span>
            </>
          ) : (
            <>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>Non sauvegardé</span>
            </>
          )}
        </div>

        {/* Export en cours */}
        {isExporting && (
          <div className="status-item status-exporting">
            <svg
              className="status-spinner"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <span>Export en cours...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const StatusBar = memo(StatusBarComponent);
export default StatusBar;
